/**
 * WordPress dependencies
 */
import { createRoot } from '@wordpress/element';

import { __ } from '@wordpress/i18n';
import BookingForm from '../components/BookingForm';

// Import CSS
import '../../css/booking-form.css';
import '../../css/event-hero.css';

import React from 'react';

/**
 * Frontend Booking Form Initialization
 *
 * This script mounts React BookingForm components in shortcode containers
 * and handles frontend-specific functionality.
 */

class BookingFormFrontend {
	constructor() {
		this.initializeBookingForms();
	}

	/**
	 * Initialize all booking forms on the page
	 */
	initializeBookingForms() {
		// Standalone [nemtly_booking_form] shortcode mounts.
		const standalone = document.querySelectorAll(
			'.nemtly-booking-form-container'
		);
		standalone.forEach( ( container ) => {
			this.mountBookingForm( container );
		} );

		// [nemtly_event] widget mounts — server-rendered hero plus a seeded
		// booking wizard in one container. React takes over the whole widget
		// and hydrates the hero from the embedded seed, so the SSR markup
		// is replaced seamlessly (no loading flash) and the wizard is driven
		// by the same data.
		const widgets = document.querySelectorAll( '.nb-event-widget' );
		widgets.forEach( ( widget ) => {
			this.mountEventWidget( widget );
		} );
	}

	/**
	 * Mount a seeded [nemtly_event] widget.
	 *
	 * @param {HTMLElement} widget The .nb-event-widget wrapper element.
	 */
	mountEventWidget( widget ) {
		const eventId = widget.getAttribute( 'data-event-id' );
		const seedAttr = widget.getAttribute( 'data-initial-event' );

		if ( ! eventId ) {
			this.showError(
				widget,
				__( 'Event ID is required', 'nemtly-booking' )
			);
			return;
		}

		let initialEvent = null;
		if ( seedAttr ) {
			try {
				initialEvent = JSON.parse( seedAttr );
			} catch ( e ) {
				initialEvent = null;
			}
		}

		const root = createRoot( widget );
		root.render(
			<BookingForm
				eventId={ parseInt( eventId, 10 ) }
				initialEvent={ initialEvent }
				onSuccess={ this.handleBookingSuccess.bind( this, widget ) }
				onError={ this.handleBookingError.bind( this, widget ) }
			/>
		);
	}

	/**
	 * Mount React BookingForm in a container
	 *
	 * @param {HTMLElement} container The container element
	 */
	mountBookingForm( container ) {
		const eventId = container.getAttribute( 'data-event-id' );
		const customClasses = container.className
			.replace( 'nemtly-booking-form-container', '' )
			.trim();

		if ( ! eventId ) {
			this.showError(
				container,
				__( 'Event ID is required', 'nemtly-booking' )
			);
			return;
		}

		// Clear loading message
		container.innerHTML = '';

		const root = createRoot( container );
		root.render(
			<BookingForm
				eventId={ parseInt( eventId, 10 ) }
				className={ customClasses }
				onSuccess={ this.handleBookingSuccess.bind( this, container ) }
				onError={ this.handleBookingError.bind( this, container ) }
			/>
		);
	}

	/**
	 * Handle successful booking
	 *
	 * @param {HTMLElement} container The container element
	 * @param {Object}      booking   The booking data
	 * @param {string}      message   Success message
	 */
	handleBookingSuccess( container, booking, message ) {
		// Dispatch custom event for external handling
		const successEvent = new CustomEvent( 'nemtlyBookingSuccess', {
			detail: { booking, message, container },
		} );
		document.dispatchEvent( successEvent );

		// Scroll to the form for better UX
		container.scrollIntoView( {
			behavior: 'smooth',
			block: 'center',
		} );

		// Optional: Analytics tracking
		if ( window.gtag ) {
			window.gtag( 'event', 'booking_success', {
				event_category: 'Nemtly Booking',
				event_label: `Event ID: ${ booking.event_id }`,
				value: 1,
			} );
		}

		// Optional: Facebook Pixel tracking
		if ( window.fbq ) {
			window.fbq( 'track', 'CompleteRegistration', {
				content_name: 'Event Booking',
				content_category: 'Event',
				content_ids: [ booking.event_id ],
				value: 1,
				currency: 'USD',
			} );
		}
	}

	/**
	 * Handle booking error
	 *
	 * @param {HTMLElement} container    The container element
	 * @param {string}      errorMessage Error message
	 */
	handleBookingError( container, errorMessage ) {
		// Dispatch custom event for external handling
		const errorEvent = new CustomEvent( 'nemtlyBookingError', {
			detail: { errorMessage, container },
		} );
		document.dispatchEvent( errorEvent );

		// Scroll to the form for better UX
		container.scrollIntoView( {
			behavior: 'smooth',
			block: 'center',
		} );

		// Optional: Analytics tracking
		if ( window.gtag ) {
			window.gtag( 'event', 'booking_error', {
				event_category: 'Nemtly Booking',
				event_label: errorMessage,
				value: 0,
			} );
		}
	}

	/**
	 * Show error message in container
	 *
	 * @param {HTMLElement} container The container element
	 * @param {string}      message   Error message
	 */
	showError( container, message ) {
		container.innerHTML = `
            <div class="nb-booking-form-error">
                <div class="nb-error-message">
                    <p class="nb-error-text"></p>
                </div>
            </div>
        `;
		container.querySelector( '.nb-error-text' ).textContent = message;
	}
}

/**
 * Initialize booking forms when DOM is ready
 */
document.addEventListener( 'DOMContentLoaded', () => {
	new BookingFormFrontend();
} );

/**
 * Re-initialize booking forms for dynamic content
 * Useful for AJAX-loaded content or SPA-style applications
 */
window.nemtlyBookingFormInit = () => {
	new BookingFormFrontend();
};

/**
 * Global event listeners for external integrations
 */

// Example: Handle booking success globally
document.addEventListener( 'nemtlyBookingSuccess', ( event ) => {
	const { booking, message } = event.detail;

	// You can add custom logic here, such as:
	// - Show additional confirmation messages
	// - Redirect to a thank you page
	// - Update shopping cart or membership status
} );

// Example: Handle booking errors globally
document.addEventListener( 'nemtlyBookingError', ( event ) => {
	const { errorMessage } = event.detail;

	// You can add custom error handling here, such as:
	// - Send error reports to analytics
	// - Show custom error notifications
	// - Trigger support chat widgets
} );

/**
 * Accessibility enhancements
 */
document.addEventListener( 'DOMContentLoaded', () => {
	// Announce form state changes to screen readers
	const originalCreateElement = document.createElement;
	document.createElement = function ( tagName ) {
		const element = originalCreateElement.call( this, tagName );

		if (
			tagName.toLowerCase() === 'div' &&
			element.className.includes( 'nb-booking-form' )
		) {
			element.setAttribute( 'role', 'form' );
			element.setAttribute(
				'aria-label',
				__( 'Event booking form', 'nemtly-booking' )
			);
		}

		return element;
	};
} );
